import { useEffect, useState } from "react";
import api from "../../services/api";
import PhoneInput, { validatePhone, formatPhone } from "../../components/PhoneInput";

const BLANK = {
  name: "", email: "", password: "",
  qualification: "", registrationNo: "",
  clinicName: "", clinicAddress: "", phone: { countryCode: "+91", number: "" },
  isActive: true,
  hasMedicineSuggestions: false,
  hasDiseaseSuggestions: false,
};

/* ─────────────────────────────────────────────────────
   ALL helper components OUTSIDE parent component.
   Inside = new type each render = input unmounts = focus lost.
───────────────────────────────────────────────────────── */

function Th({ children }) {
  return (
    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
      {children}
    </th>
  );
}

function Field({ label, value, onChange, type, span, error }) {
  return (
    <div className={span ? "col-span-2" : ""}>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type={type || "text"}
        className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 transition ${
          error ? "border-red-400 focus:ring-red-400" : "border-gray-200 focus:ring-green-400"
        }`}
        value={value}
        onChange={e => onChange(e.target.value)}
        autoComplete="off"
      />
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
}

function PhoneField({ label, value, onChange, error, span }) {
  return (
    <div className={span ? "col-span-2" : ""}>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type="tel"
        className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 transition ${
          error ? "border-red-400 focus:ring-red-400" : "border-gray-200 focus:ring-green-400"
        }`}
        value={value}
        maxLength={10}
        placeholder="10-digit number"
        onChange={e => onChange(e.target.value.replace(/\D/g, ""))}
        autoComplete="off"
      />
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
}

function PasswordField({ label, value, onChange, error, span }) {
  const [show, setShow] = useState(false);
  return (
    <div className={span ? "col-span-2" : ""}>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          className={`w-full border rounded-xl px-4 py-2.5 pr-11 text-sm focus:outline-none focus:ring-2 transition ${
            error ? "border-red-400 focus:ring-red-400" : "border-gray-200 focus:ring-green-400"
          }`}
          value={value}
          onChange={e => onChange(e.target.value)}
          autoComplete="new-password"
        />
        <button
          type="button"
          onClick={() => setShow(p => !p)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg leading-none"
          tabIndex={-1}
        >
          {show ? "🙈" : "👁️"}
        </button>
      </div>
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-800">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ModalActions({ saving, onCancel, onSave, label }) {
  return (
    <div className="flex gap-3 pt-2">
      <button
        type="button"
        onClick={onCancel}
        className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition"
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-xl text-sm font-semibold hover:bg-green-700 disabled:opacity-60 transition"
      >
        {saving ? "Saving…" : label}
      </button>
    </div>
  );
}

/* ── Validation ── */
function validateDoctor(form, isEdit) {
  const e = {};
  if (!form.name.trim())
    e.name = "Name is required";
  else if (!/^[a-zA-Z\s'.\\-]+$/.test(form.name.trim()))
    e.name = "Name should contain only letters and spaces";

  if (!form.email.trim())
    e.email = "Email is required";
  else if (!/\S+@\S+\.\S+/.test(form.email))
    e.email = "Enter a valid email address";

  if (!isEdit && !form.password)
    e.password = "Password is required";
  if (form.password && form.password.length < 6)
    e.password = "Password must be at least 6 characters";

  if (!form.qualification.trim())
    e.qualification = "Qualification is required";

  if (!form.registrationNo.trim())
    e.registrationNo = "Registration number is required";

  if (!form.clinicName.trim())
    e.clinicName = "Clinic name is required";

  const phoneErr = validatePhone(form.phone, false);
  if (phoneErr) e.phone = phoneErr;

  return e;
}

/* ── Main component ── */
export default function AdminDoctors() {
  const [doctors,  setDoctors]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [form,     setForm]     = useState(BLANK);
  const [errors,   setErrors]   = useState({});
  const [saving,   setSaving]   = useState(false);
  const [apiError, setApiError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/doctors-list");
      setDoctors(res.data || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null); setForm(BLANK); setErrors({}); setApiError(""); setShowForm(true);
  };

  const openEdit = (d) => {
    setEditing(d);
    // Parse stored phone (could be "+91 9876543210" or plain "9876543210")
    let phoneVal = { countryCode: "+91", number: "" };
    if (d.phone) {
      const match = d.phone.match(/^(\+\d{1,4})\s*(\d+)$/);
      if (match) phoneVal = { countryCode: match[1], number: match[2] };
      else phoneVal = { countryCode: "+91", number: d.phone.replace(/\D/g, "") };
    }
    setForm({
      name: d.name, email: d.email, password: "",
      qualification: d.qualification || "", registrationNo: d.registrationNo || "",
      clinicName: d.clinicName || "", clinicAddress: d.clinicAddress || "",
      phone: phoneVal, isActive: d.isActive,
      hasMedicineSuggestions: d.hasMedicineSuggestions || false,
      hasDiseaseSuggestions: d.hasDiseaseSuggestions || false,
    });
    setErrors({}); setApiError(""); setShowForm(true);
  };

  const save = async () => {
    const errs = validateDoctor(form, !!editing);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSaving(true);
    setApiError("");
    try {
      const payload = { ...form, phone: formatPhone(form.phone) };
      if (!payload.password) delete payload.password;

      if (editing) {
        await api.put(`/admin/doctors/${editing._id}`, payload);
      } else {
        await api.post("/admin/create-doctor", payload);
      }
      setShowForm(false);
      load();
    } catch (e) {
      setApiError(e.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this doctor? Their patients and prescriptions will remain.")) return;
    try {
      await api.delete(`/admin/doctors/${id}`);
      if (editing?._id === id) { setEditing(null); setShowForm(false); }
      load();
    }
    catch { alert("Delete failed"); }
  };

  const toggleActive = async (d) => {
    try {
      await api.put(`/admin/doctors/${d._id}`, { isActive: !d.isActive });
      load();
    } catch { alert("Update failed"); }
  };

  const setField = (k, v) => {
    setForm(p => ({ ...p, [k]: v }));
    if (errors[k]) setErrors(p => ({ ...p, [k]: "" }));
  };

  return (
    <div className="space-y-6 max-w-6xl">

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">🩺 Doctors</h1>
          <p className="text-sm text-gray-500">
            Manage doctor login accounts. Each doctor has their own prescription settings and patient list.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="px-5 py-2.5 bg-green-600 text-white rounded-xl text-sm font-semibold hover:bg-green-700 transition"
        >
          + Add Doctor
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <p className="text-center py-12 text-gray-400 text-sm">Loading…</p>
        ) : doctors.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <p className="text-4xl mb-3">🩺</p>
            <p className="text-sm font-medium">No doctors yet</p>
            <p className="text-xs mt-1">Add your first doctor to enable the OPD prescription system.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <Th>Doctor</Th><Th>Email</Th><Th>Qualification</Th><Th>Clinic</Th><Th>Status</Th><Th>💊 Suggest</Th><Th>Actions</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {doctors.map(d => (
                  <tr key={d._id} className="hover:bg-gray-50 transition">
                    <td className="px-5 py-3.5">
                      <p className="font-medium text-gray-800">{d.name}</p>
                      <p className="text-xs text-gray-400">{d.phone}</p>
                    </td>
                    <td className="px-5 py-3.5 text-gray-500">{d.email}</td>
                    <td className="px-5 py-3.5 text-gray-500">{d.qualification}</td>
                    <td className="px-5 py-3.5 text-gray-500">{d.clinicName}</td>
                    <td className="px-5 py-3.5">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        d.isActive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"
                      }`}>
                        {d.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      <button
                        type="button"
                        title={d.hasMedicineSuggestions ? "Revoke medicine suggestions" : "Grant medicine suggestions"}
                        onClick={async () => {
                          try {
                            await api.put(`/admin/doctors/${d._id}`, { hasMedicineSuggestions: !d.hasMedicineSuggestions });
                            load();
                          } catch { alert("Update failed"); }
                        }}
                        style={{
                          background: d.hasMedicineSuggestions ? "#DCFCE7" : "#F3F4F6",
                          border: `1.5px solid ${d.hasMedicineSuggestions ? "#86EFAC" : "#D1D5DB"}`,
                          color: d.hasMedicineSuggestions ? "#166534" : "#9CA3AF",
                          borderRadius: 20,
                          padding: "3px 10px",
                          fontSize: 11,
                          fontWeight: 700,
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {d.hasMedicineSuggestions ? "✓ On" : "Off"}
                      </button>
                      <button
                        onClick={async e => {
                          e.stopPropagation();
                          await api.put(`/admin/doctors/${d._id}`, { hasDiseaseSuggestions: !d.hasDiseaseSuggestions });
                          setDoctors(prev => prev.map(doc => doc._id === d._id ? { ...doc, hasDiseaseSuggestions: !doc.hasDiseaseSuggestions } : doc));
                        }}
                        title={d.hasDiseaseSuggestions ? "Revoke disease suggestions" : "Grant disease suggestions"}
                        style={{
                          padding: "4px 10px", borderRadius: 8, cursor: "pointer",
                          fontSize: 11, fontWeight: 700,
                          background: d.hasDiseaseSuggestions ? "#F5F3FF" : "#F3F4F6",
                          border: `1.5px solid ${d.hasDiseaseSuggestions ? "#C4B5FD" : "#D1D5DB"}`,
                          color: d.hasDiseaseSuggestions ? "#5B21B6" : "#9CA3AF",
                          transition: "all 0.15s",
                        }}
                      >
                        🔬 Disease<br/>{d.hasDiseaseSuggestions ? "✓ On" : "Off"}
                      </button>
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex gap-3 flex-wrap">
                        <button type="button" onClick={() => openEdit(d)}     className="text-blue-500 hover:text-blue-700 text-xs font-medium">Edit</button>
                        <button type="button" onClick={() => toggleActive(d)} className="text-orange-500 hover:text-orange-700 text-xs font-medium">
                          {d.isActive ? "Deactivate" : "Activate"}
                        </button>
                        <button type="button" onClick={() => del(d._id)}      className="text-red-400 hover:text-red-600 text-xs font-medium">Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info box */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-xs text-blue-700 space-y-1">
        <p className="font-semibold">ℹ️ Multi-doctor setup</p>
        <p>Each doctor logs in at <strong>/staff-portal/login</strong> with their own email and password.</p>
        <p>Prescription settings, patient lists, and OPD queue are <strong>per-doctor</strong> — they never overlap.</p>
        <p>To seed the first doctor for testing: <code className="bg-blue-100 px-1 rounded">/api/doctor/seed-doctor</code></p>
      </div>

      {/* Form modal */}
      {showForm && (
        <Modal title={editing ? "Edit Doctor" : "Add Doctor"} onClose={() => setShowForm(false)}>
          {apiError && (
            <div className="bg-red-50 border border-red-200 text-red-600 rounded-xl px-4 py-2 text-sm">
              {apiError}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <Field
              label="Full Name *"
              value={form.name}
              onChange={v => setField("name", v)}
              error={errors.name}
              span
            />
            <Field
              label="Email *"
              value={form.email}
              onChange={v => setField("email", v)}
              type="email"
              error={errors.email}
              span
            />
            <PasswordField
              label={editing ? "New Password (leave blank to keep)" : "Password *"}
              value={form.password}
              onChange={v => setField("password", v)}
              error={errors.password}
              span
            />
            <Field
              label="Qualification (MBBS/BAMS…) *"
              value={form.qualification}
              onChange={v => setField("qualification", v)}
              error={errors.qualification}
            />
            <Field
              label="Registration No *"
              value={form.registrationNo}
              onChange={v => setField("registrationNo", v)}
              error={errors.registrationNo}
            />
            <Field
              label="Clinic / Hospital Name *"
              value={form.clinicName}
              onChange={v => setField("clinicName", v)}
              error={errors.clinicName}
              span
            />
            <Field
              label="Clinic Address"
              value={form.clinicAddress}
              onChange={v => setField("clinicAddress", v)}
              span
            />
            <PhoneInput
              label="Phone"
              value={form.phone}
              onChange={v => setField("phone", v)}
              error={errors.phone}
            />
          </div>

          <div className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              id="isActive"
              checked={form.isActive}
              onChange={e => setField("isActive", e.target.checked)}
            />
            <label htmlFor="isActive" className="text-gray-700 font-medium">Active (can login)</label>
          </div>

          <div className="flex items-center gap-2 text-sm bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
            <input
              type="checkbox"
              id="hasMedicineSuggestions"
              checked={form.hasMedicineSuggestions}
              onChange={e => setField("hasMedicineSuggestions", e.target.checked)}
            />
            <label htmlFor="hasMedicineSuggestions" className="text-blue-800 font-semibold">
              💊 Enable Medicine Suggestion Panel
            </label>
            <span className="text-blue-500 text-xs ml-1">(Master admin access only)</span>
          </div>

          <div className="flex items-center gap-2 p-3 bg-purple-50 border border-purple-200 rounded-xl">
            <input
              type="checkbox"
              id="hasDiseaseSuggestions"
              checked={form.hasDiseaseSuggestions}
              onChange={e => setField("hasDiseaseSuggestions", e.target.checked)}
            />
            <label htmlFor="hasDiseaseSuggestions" className="text-purple-800 font-semibold">
              🔬 Enable Disease Suggestion Panel
            </label>
            <span className="text-purple-500 text-xs ml-1">(Master admin access only)</span>
          </div>

          <ModalActions
            saving={saving}
            onCancel={() => setShowForm(false)}
            onSave={save}
            label={editing ? "Save Changes" : "Add Doctor"}
          />
        </Modal>
      )}

    </div>
  );
}